import { useState } from 'react';

import type {
  HitlActionRequest,
  HitlActionType,
  HitlItem,
  SeriesCadenceCandidate,
} from '../services/types';

import { Button } from './Button';

export interface SeriesCadenceCardProps {
  hit: HitlItem;
  onAction: (hitId: string, action: HitlActionRequest) => Promise<void>;
  reviewerId: string;
}

type Cadence =
  | 'daily'
  | 'weekly'
  | 'monthly'
  | 'quarterly'
  | 'yearly'
  | 'inferred'
  | 'no_resample';
type Method = 'linear' | 'ffill' | 'bfill' | 'drop';

const CADENCE_COPY: Record<Cadence, { label: string; detail: string }> = {
  daily: {
    label: 'Daily',
    detail:
      'Insert any missing day between the first and last observation. Use when the column tracks a daily metric (e.g. revenue, signups).',
  },
  weekly: {
    label: 'Weekly',
    detail:
      'Insert any missing week between observations. Anchored on the same weekday as the first observed row.',
  },
  monthly: {
    label: 'Monthly',
    detail:
      'Insert any missing calendar month between observations. Anchored on the first day of the month.',
  },
  // v0.6 Stage H.1 — quarterly + yearly
  quarterly: {
    label: 'Quarterly',
    detail:
      'Insert any missing calendar quarter between observations. Anchored on the same month as the first observed row (so Feb/May/Aug/Nov-anchored series stay aligned, not snapped to Jan/Apr/Jul/Oct).',
  },
  yearly: {
    label: 'Yearly',
    detail:
      'Insert any missing calendar year between observations. Anchored on the same month as the first observed row. Ideal for annual KPIs / fiscal-year metrics.',
  },
  inferred: {
    label: 'Inferred from data',
    detail:
      "Use the median gap between observations as the target step. Recommended when the cadence is regular but doesn't fit neatly into one of the standard windows.",
  },
  no_resample: {
    label: "Don't resample — keep rows as-is",
    detail:
      'Preserve the original rows verbatim. Use when the column captures discrete events (event log, irregular snapshots) rather than a uniform time series.',
  },
};

const METHOD_COPY: Record<Method, { label: string; detail: string }> = {
  linear: {
    label: 'Linear interpolation',
    detail:
      'Inserted rows take the straight-line midpoint between the surrounding observations. Best for continuous metrics (revenue, sensor readings).',
  },
  ffill: {
    label: 'Forward-fill',
    detail:
      'Inserted rows carry the prior known value forward. Best for snapshot-style metrics (account balance, headcount).',
  },
  bfill: {
    label: 'Backward-fill',
    detail:
      'Inserted rows take the next known value. Use when you trust the next observation more than the prior.',
  },
  drop: {
    label: 'Leave the cell empty',
    detail:
      'Insert the row to make the cadence uniform but leave the value cell blank for downstream triage.',
  },
};

const CADENCE_ORDER: Cadence[] = [
  'daily',
  'weekly',
  'monthly',
  'quarterly',
  'yearly',
  'inferred',
  'no_resample',
];

const METHOD_ORDER: Method[] = ['linear', 'ffill', 'bfill', 'drop'];

/**
 * v0.5 Stage F.3 — UI card for the ``series_cadence`` HITL.
 *
 * Fired by MOA's ``_resample_series_columns`` stage when:
 *   * the table has exactly one date/datetime column with paired
 *     numeric value columns, AND
 *   * ``infer_cadence`` reports the date column looks like a
 *     uniform-cadence series (variance_ratio < 0.20 with parsed
 *     count >= 3), AND
 *   * the reviewer hasn't yet picked a ``series_resample`` policy
 *     for this ``(file_hash, date_column)`` pair.
 *
 * Reviewer picks one of seven cadences (daily / weekly / monthly /
 * quarterly / yearly / inferred / no_resample — the last two added in
 * v0.6 Stage H.1) plus an interpolation method (linear /
 * ffill / bfill / drop). Backend writes a ``column_policy`` audit
 * row keyed on ``(file_hash, date_column)``, threads the spec
 * through CPA → MOA → resample stage, and the stage applies the
 * chosen cadence + method on this run.
 *
 * Lives outside HitlQueue.tsx so the existing column-datatype /
 * format-decision / table-boundary / cross-field-rule /
 * unit-conversion / json-flatten card paths stay untouched.
 */
export function SeriesCadenceCard({
  hit,
  onAction,
  reviewerId,
}: SeriesCadenceCardProps) {
  const candidates = hit.candidates as
    | Array<SeriesCadenceCandidate>
    | null
    | undefined;
  const candidate =
    candidates && candidates.length > 0 ? candidates[0] : null;

  const [pending, setPending] = useState<HitlActionType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resolved, setResolved] = useState(false);
  const [cadence, setCadence] = useState<Cadence | null>(
    (candidate?.suggested_cadence as Cadence) || null,
  );
  const [method, setMethod] = useState<Method>(
    ((candidate?.suggested_method as Method) || 'linear') as Method,
  );

  const onConfirm = async () => {
    if (!cadence) return;
    setPending('choose_series_cadence');
    setError(null);
    try {
      await onAction(hit.hit_id, {
        type: 'choose_series_cadence',
        payload: { cadence, method },
        reviewer_id: reviewerId,
      });
      setResolved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setPending(null);
    }
  };

  const valueColumns = candidate?.value_columns ?? [];
  const inferred = candidate?.inferred_cadence ?? '';
  const medianDelta = candidate?.median_delta_days;
  const parsedCount = candidate?.parsed_count;

  return (
    <li
      data-rule-card="series-cadence"
      data-resolution-type="series_cadence"
      className={`rounded-md border bg-paper border-l-4 border-l-warn border-border p-4 space-y-3 ${
        resolved ? 'opacity-60' : ''
      }`}
      aria-busy={pending !== null}
    >
      <div className="space-y-1">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <h3 className="m-0 text-sm font-semibold flex-1">
            <code>{hit.column}</code> · pick cadence + interpolation
          </h3>
          <span className="text-xs uppercase font-semibold rounded-sm px-2 py-0.5 bg-surface-alt text-muted whitespace-nowrap">
            series cadence
          </span>
        </div>
        {inferred && medianDelta !== undefined && parsedCount !== undefined && (
          <p className="text-xs text-muted m-0">
            Looks like a <strong className="text-ink">{inferred}</strong>{' '}
            series — median gap{' '}
            <strong className="text-ink">
              {medianDelta.toFixed(1)} days
            </strong>{' '}
            across {parsedCount} rows.
          </p>
        )}
        {valueColumns.length > 0 && (
          <p className="text-[11px] text-muted m-0">
            Value columns:{' '}
            <span className="font-mono">
              {valueColumns.join(', ')}
            </span>
          </p>
        )}
      </div>

      {resolved ? (
        <p className="text-sm text-muted">Resolved.</p>
      ) : (
        <>
          <fieldset className="space-y-2">
            <legend className="text-xs uppercase tracking-wide text-muted">
              Cadence
            </legend>
            {CADENCE_ORDER.map((c) => {
              const copy = CADENCE_COPY[c];
              return (
                <label
                  key={c}
                  className="flex items-start gap-3 rounded border border-border p-2 cursor-pointer hover:bg-surface-alt/30"
                >
                  <input
                    type="radio"
                    name={`series-cadence-${hit.hit_id}`}
                    value={c}
                    checked={cadence === c}
                    onChange={() => setCadence(c)}
                    className="mt-0.5"
                    data-testid={`series-cadence-${c}`}
                  />
                  <span className="flex-1">
                    <span className="block font-medium text-sm">
                      {copy.label}
                    </span>
                    <span className="block text-xs text-muted mt-0.5">
                      {copy.detail}
                    </span>
                  </span>
                </label>
              );
            })}
          </fieldset>

          <div className="space-y-1">
            <label
              htmlFor={`series-method-${hit.hit_id}`}
              className="text-xs uppercase tracking-wide text-muted block"
            >
              Interpolation method
            </label>
            <select
              id={`series-method-${hit.hit_id}`}
              data-testid="series-method"
              value={method}
              onChange={(e) => setMethod(e.target.value as Method)}
              className="w-full rounded border border-border bg-paper px-2 py-1 text-sm"
              disabled={cadence === 'no_resample'}
            >
              {METHOD_ORDER.map((m) => (
                <option key={m} value={m}>
                  {METHOD_COPY[m].label}
                </option>
              ))}
            </select>
            {cadence !== 'no_resample' && (
              <p className="text-[11px] text-muted m-0">
                {METHOD_COPY[method].detail}
              </p>
            )}
            {cadence === 'no_resample' && (
              <p className="text-[11px] text-muted m-0">
                No interpolation — original rows preserved verbatim.
              </p>
            )}
          </div>

          {error && <p className="text-sm text-danger">{error}</p>}
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="primary"
              onClick={onConfirm}
              disabled={cadence === null || pending !== null}
              data-testid="series-cadence-confirm"
            >
              {pending === 'choose_series_cadence' ? 'Saving…' : 'Confirm'}
            </Button>
          </div>
        </>
      )}
    </li>
  );
}
